<script setup lang="ts">
/* Single KPI on the dashboard. */
import Icon from '@/components/Icon.vue'
import type { IconName } from '@/components/icons'

withDefaults(
  defineProps<{
    label: string
    /** Rendered as-is — format numbers at the call site. */
    value: string
    icon?: IconName
    /** Small line under the value, e.g. "vs. last month". */
    note?: string
    /** Dims the tile and shows a placeholder bar instead of the value. */
    loading?: boolean
  }>(),
  { loading: false },
)
</script>

<template>
  <div class="tile">
    <div class="top">
      <span class="label">{{ label }}</span>
      <span v-if="icon" class="icon">
        <Icon :name="icon" :size="16" />
      </span>
    </div>
    <span v-if="loading" class="skeleton" aria-hidden="true" />
    <p v-else class="value">{{ value }}</p>
    <p v-if="note" class="note">{{ note }}</p>
  </div>
</template>

<style scoped>
.tile {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
  padding: var(--space-sm) var(--space-md);
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2xs);
}

.label {
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
}

.icon {
  display: grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--color-accent);
  background-color: var(--color-accent-soft);
  border-radius: var(--radius-sm);
}

.value {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--color-heading);
}

.note {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

.skeleton {
  display: block;
  width: 4ch;
  height: var(--text-xl);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-xs);
}
</style>
